// src/components/Navbar.js
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSubscription } from "../context/SubscriptionContext";
import { useTour } from "../context/TourContext";
import appLogo from "../assets/nexus-logo-only.png";
import { UserProfile } from "./UserProfile";
import {
  LayoutDashboard,
  Package,
  Lock,
  Truck,
  Receipt,
  Wallet,

  Box,
  Barcode,
  Globe,
  Shield,
  ChevronDown,
  // ChevronUp removed — single ChevronDown that rotates 180° on open
  // gives a smoother animation than swapping between two icons.
  PanelLeftClose,
  PanelLeft,
  Users,
  Lightbulb,
  Mail,
  FileText,
  List,
  Search,
} from "lucide-react";
import NotificationBell from "./NotificationBell";

// Allowed user IDs for Listings feature (beta/testing)
const ALLOWED_LISTING_USERS = [
  'ac35f1f3-87c6-4669-98f0-5c3ed4189751',
];

/* ============================================================
 * Nav subcomponents — defined at MODULE scope (not inside Navbar)
 *
 * Why this matters for the dropdown animations:
 * Previously these lived as inline `const NavDropdown = ...` declarations
 * inside the Navbar function body. Every Navbar re-render (which happens
 * on every dropdown toggle, route change, sidebar collapse, etc.) created
 * brand-new component identities for NavDropdown/NavItem/DropdownItem.
 * React's reconciler sees a new function reference at the same JSX
 * position as a completely different component — so it unmounted the
 * old dropdown DOM and mounted a fresh one.
 *
 * Result: the chevron's DOM node was BRAND NEW each toggle, with no
 * prior `transform` state for CSS to interpolate from. Transitions
 * silently skipped to the end state every time.
 *
 * Hoisting to module scope gives stable function references, React
 * reuses the same DOM nodes across renders, and the transition has
 * something to animate between. Same fix unlocks the dropdown <ul>
 * expand/collapse animation that was also being skipped.
 * ============================================================ */

const NavItem = ({ to, icon: Icon, label, end = false, tourId, isCollapsed, closeNav }) => (
  <NavLink
    to={to}
    end={end}
    onClick={(e) => {
      // Only close nav on primary (left) click, not middle-click
      if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
        closeNav();
      }
    }}
    title={label}
    aria-label={label}
    data-tour={tourId}
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-150 group
      ${isActive
        ? "bg-[rgb(var(--accent-primary))]/10 text-[rgb(var(--accent-primary))] font-medium"
        : "text-theme-muted hover:bg-theme-hover hover:text-theme"
      }
      ${isCollapsed ? "justify-center" : ""}`
    }
  >
    <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.75} />
    {!isCollapsed && <span className="truncate">{label}</span>}
  </NavLink>
);

const NavDropdown = ({ id, icon: Icon, label, children, items, tourId, isOpen, onToggle, isCollapsed, closeNav, pathname }) => {
  const hasActiveChild = items?.some(item => pathname === item.to);

  return (
    <div className="dropdown" data-tour={tourId}>
      <div className="flex items-center">
        <NavLink
          to={items?.[0]?.parentTo || `/${id}`}
          onClick={(e) => {
            if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
              closeNav();
            }
          }}
          title={label}
          aria-label={label}
          className={({ isActive }) =>
            `flex-1 flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg transition-all duration-150
            ${isActive || hasActiveChild
              ? "bg-[rgb(var(--accent-primary))]/10 text-[rgb(var(--accent-primary))] font-medium"
              : "text-theme-muted hover:bg-theme-hover hover:text-theme"
            }
            ${isCollapsed ? "justify-center" : ""}`
          }
        >
          <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.75} />
          {!isCollapsed && <span className="truncate">{label}</span>}
        </NavLink>
        {!isCollapsed && (
          <button
            type="button"
            className={`p-1.5 rounded-md transition-colors
              ${isOpen
                ? "text-theme"
                : "text-theme-muted hover:text-theme"
              }
              hover:bg-theme-hover`}
            aria-haspopup="true"
            aria-expanded={isOpen}
            aria-controls={`${id}-menu`}
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            title={`${isOpen ? "Close" : "Open"} ${label} menu`}
          >
            {/* Single chevron that rotates 180° between states. The explicit
                `rotate-0` in the closed state is required: CSS can't
                interpolate from `transform: none` to a transform value, so
                without it the rotation snaps instead of animating. With both
                rotate-0 and rotate-180 in the transform stack, the browser
                has two valid transforms to tween between. */}
            <ChevronDown
              className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : 'rotate-0'}`}
            />
          </button>
        )}
      </div>

      {/* Smooth dropdown */}
      <ul
        id={`${id}-menu`}
        role="menu"
        style={{ borderColor: 'var(--color-border)' }}
        className={`ml-4 pl-4 mt-1 space-y-0.5 overflow-hidden border-l
          transition-all duration-200 ease-out
          ${isOpen ? "max-h-40 opacity-100" : "max-h-0 opacity-0"}`}
      >
        {children}
      </ul>
    </div>
  );
};

const DropdownItem = ({ to, icon: Icon, label, closeNav }) => (
  <li role="none">
    <NavLink
      role="menuitem"
      to={to}
      onClick={(e) => {
        if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
          closeNav();
        }
      }}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-all duration-150
        ${isActive
          ? "bg-[rgb(var(--accent-primary))]/10 text-[rgb(var(--accent-primary))] font-medium"
          : "text-theme-muted hover:bg-theme-hover hover:text-theme"
        }`
      }
    >
      <Icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
      <span>{label}</span>
    </NavLink>
  </li>
);

const Navbar = ({ isCollapsed, toggleCollapse, isNavOpen, closeNav, onSearchClick }) => {
  const { currentUser, isAdmin } = useAuth();
  // Plus / Lifetime get a "+" appended to the brand wordmark. isPro is
  // the unified "any paid tier" flag from SubscriptionContext (covers
  // Plus monthly, Plus yearly, and Lifetime via LEGACY_TIER_MAP).
  const { subscriptionStatus } = useSubscription();
  const isPlusUser = subscriptionStatus?.isPro;

  // Check if current user has access to listings
  const hasListingsAccess = currentUser && ALLOWED_LISTING_USERS.includes(currentUser.uid);
  const { restartTour, currentStepTarget, tourActive } = useTour();
  const navRef = useRef(null);
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState(null);

  // Close dropdowns on route change
  useEffect(() => setOpenDropdown(null), [location.pathname]);

  // Auto-open the right nav dropdown when the tour spotlights the
  // matching item. data-tour="nav-inventory" / "nav-sales" are on the
  // dropdown WRAPPERS (NavDropdown root) — opening the dropdown
  // expands the spotlight to include the sub-items (Vault, etc.) so the
  // user sees them revealed without having to interact.
  // Closes any tour-opened dropdown when stepping onto a non-dropdown
  // nav so we don't leave inventory + sales both open mid-tour.
  // Matching on the target string is stable across tier-aware step
  // reordering.
  useEffect(() => {
    if (!tourActive) return;
    let dropdown = null;
    if (currentStepTarget === '[data-tour="nav-inventory"]') {
      dropdown = 'inventory';
    } else if (currentStepTarget === '[data-tour="nav-sales"]') {
      dropdown = 'sales';
    }
    setOpenDropdown(dropdown);
    if (dropdown) {
      // Force joyride to recalculate the spotlight's bbox after the
      // dropdown expands the target wrapper. joyride positions the
      // spotlight on step-show using getBoundingClientRect, then only
      // re-measures on window resize. Dispatching a resize event after
      // React renders the open dropdown gets the spotlight to grow to
      // include the Vault sub-item instead of staying pinned at the
      // closed button's small size.
      const t = setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 80);
      return () => clearTimeout(t);
    }
  }, [tourActive, currentStepTarget]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handler = (e) => {
      if (!navRef.current) return;
      if (!e.target.closest(".dropdown")) setOpenDropdown(null);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const toggle = (key) => setOpenDropdown((prev) => (prev === key ? null : key));

  // NavItem / NavDropdown / DropdownItem now live at module scope (above)
  // so React reuses their DOM nodes across re-renders — that's what makes
  // the chevron rotation animate instead of snapping. Call sites below
  // pass the dependencies that used to come from closure as props.

  return (
    <>
      {/* Desktop sidebar only - hidden on mobile (we use MobileNavBar instead).
          Glassmorphic surface: heavy transparency + strong blur lets the
          ambient body-level glow read through the sidebar, with a barely-
          there accent tint layered on top so the sidebar still feels like
          it belongs to the app's color theme. Tuned via /gradient-playground. */}
      <nav
        ref={navRef}
        style={{
          background:
            'linear-gradient(0deg, ' +
              'color-mix(in srgb, rgb(var(--accent-primary)) 1%, transparent), ' +
              'color-mix(in srgb, rgb(var(--accent-primary)) 1%, transparent)' +
            '), color-mix(in srgb, var(--color-card) 25%, transparent)',
          backdropFilter: 'blur(20px) saturate(75%)',
          WebkitBackdropFilter: 'blur(20px) saturate(75%)',
          borderColor: 'var(--color-border)'
        }}
        className={`hidden lg:flex flex-col fixed inset-y-0 left-0 z-[1100]
          border-r
          transition-all duration-200 ease-out
          ${isCollapsed ? "lg:w-16" : "lg:w-60"}`}
      >
      {/* Header (logo + collapse toggle) */}
      <div style={{ borderColor: 'var(--color-border)' }} className={`flex items-center p-3 border-b ${isCollapsed ? "flex-col gap-2" : "justify-between"}`}>
        <div className={`flex items-center gap-3`}>
          <img
            src={appLogo}
            alt="Nexus"
            className="w-8 h-8 transition-all duration-200"
          />
          {!isCollapsed && (
            <div className="flex flex-col">
              <div className="flex items-center gap-1">
                {/* Brand wordmark. Letter-spacing was 0.1em (tracking-widest)
                    until 2026-05-30 — reduced to 0.05em for a tighter,
                    less-spaced read in the nav rail. The HomePage / legal-
                    page shell still use 0.1em; the nav is its own context.
                    Plus users get an accent-colored "+" appended (with
                    normal letter-spacing so the spacing rules don't push
                    it away from the N of Nexus). */}
                <span
                  className="text-sm font-medium text-theme"
                  style={{ letterSpacing: '0.05em' }}
                >
                  Nexus
                </span>
                {isPlusUser && (
                  <span
                    className="text-sm font-semibold"
                    style={{
                      color: 'rgb(var(--accent-primary))',
                      letterSpacing: 'normal',
                      // Negative margin pulls the + tight against the
                      // 's' of Nexus — counteracts the 0.05em trailing
                      // letter-space the parent adds after every letter
                      // (including the final 's'), which otherwise
                      // makes the + visually float ~3px away.
                      marginLeft: '-3px',
                    }}
                    aria-label="Plus subscriber"
                    title="Nexus Plus"
                  >
                    +
                  </span>
                )}
                {/* Tour restart button */}
                <button
                  onClick={restartTour}
                  aria-label="Restart tour"
                  title="Restart tour"
                  data-tour="restart-tour"
                  className="p-0.5 rounded text-zinc-400 hover:text-white hover:bg-white/10
                    transition-colors duration-150"
                >
                  <Lightbulb className="w-3 h-3" strokeWidth={1.5} />
                </button>
              </div>
              <span className="text-[10px] text-theme-muted">Inventory Manager</span>
            </div>
          )}
        </div>

        {/* Toggle button */}
        <button
          onClick={toggleCollapse}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="p-1.5 rounded-lg text-theme-muted hover:text-theme hover:bg-theme-hover
            transition-colors duration-150"
        >
          {isCollapsed ? (
            <PanelLeft className="w-5 h-5" strokeWidth={1.5} />
          ) : (
            <PanelLeftClose className="w-5 h-5" strokeWidth={1.5} />
          )}
        </button>
      </div>


      {/* Search Button */}
      {onSearchClick && (
        <div className="px-2 pt-2">
          {isCollapsed ? (
            <button
              onClick={onSearchClick}
              className="mx-auto flex items-center justify-center w-10 h-10 rounded-lg transition-colors text-theme-muted hover:text-theme hover:bg-theme-hover"
              title="Search (Ctrl+K)"
            >
              <Search className="w-5 h-5" strokeWidth={1.5} />
            </button>
          ) : (
            <button
              onClick={onSearchClick}
              className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs transition-colors text-theme-muted hover:bg-theme-hover"
              style={{ border: '1px solid var(--color-border)' }}
            >
              <Search className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
              <span className="flex-1 text-left">Search...</span>
              <kbd className="text-[9px] px-1 py-0.5 rounded font-mono" style={{ backgroundColor: 'var(--color-card-hover)', border: '1px solid var(--color-border)' }}>
                Ctrl K
              </kbd>
            </button>
          )}
        </div>
      )}

      {/* Navigation Links */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {/* Section label */}
        {!isCollapsed && (
          <div className="px-3 py-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-theme-muted">
              Main
            </span>
          </div>
        )}

        <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" end tourId="nav-dashboard" isCollapsed={isCollapsed} closeNav={closeNav} />

        {/* Inventory dropdown */}
        <NavDropdown
          id="inventory"
          icon={Package}
          label="Inventory"
          tourId="nav-inventory"
          items={[
            { to: "/vault", parentTo: "/inventory" },
            // eBay integration disabled - uncomment when ready for production
            // ...(hasListingsAccess ? [{ to: "/listings" }] : [])
          ]}
          isOpen={openDropdown === "inventory"}
          onToggle={() => toggle("inventory")}
          isCollapsed={isCollapsed}
          closeNav={closeNav}
          pathname={location.pathname}
        >
          <DropdownItem to="/vault" icon={Lock} label="Vault" closeNav={closeNav} />
          {/* eBay integration disabled - uncomment when ready for production */}
          {/* {hasListingsAccess && <DropdownItem to="/listings" icon={List} label="Listings" closeNav={closeNav} />} */}
        </NavDropdown>

        {/* Sales dropdown */}
        <NavDropdown
          id="sales"
          icon={Receipt}
          label="Sales"
          tourId="nav-sales"
          items={[{ to: "/buyers", parentTo: "/sales" }]}
          isOpen={openDropdown === "sales"}
          onToggle={() => toggle("sales")}
          isCollapsed={isCollapsed}
          closeNav={closeNav}
          pathname={location.pathname}
        >
          <DropdownItem to="/buyers" icon={Users} label="Buyers" closeNav={closeNav} />
        </NavDropdown>

        {/* Expenses */}
        <NavItem to="/expenses" icon={Wallet} label="Expenses" tourId="nav-expenses" isCollapsed={isCollapsed} closeNav={closeNav} />

        {!isCollapsed && (
          <div className="px-3 pt-4 pb-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-theme-muted">
              Tools
            </span>
          </div>
        )}

        {/* /packages was merged into /orders-tracking. The old
            tourId="nav-packages" target moved here, renamed
            "nav-orders-tracking" to match the new route. */}
        <NavItem to="/orders-tracking" icon={Truck} label="Orders & Tracking" tourId="nav-orders-tracking" isCollapsed={isCollapsed} closeNav={closeNav} />
        {/* <NavItem to="/email-scraper" icon={Mail} label="Email Scraper" /> */}
        <NavItem to="/product-scanner" icon={Barcode} label="My Products" tourId="nav-scanner" isCollapsed={isCollapsed} closeNav={closeNav} />
        <NavItem to="/reports" icon={FileText} label="Reports" tourId="nav-reports" isCollapsed={isCollapsed} closeNav={closeNav} />

        {/* Admin-only links */}
        {isAdmin && (
          <>
            <NavItem to="/global-products-admin" icon={Globe} label="Global Products" isCollapsed={isCollapsed} closeNav={closeNav} />
            <NavItem to="/admin-settings" icon={Shield} label="Admin Settings" isCollapsed={isCollapsed} closeNav={closeNav} />
          </>
        )}
      </div>

      {/* Footer with user profile and notification bell */}
      <div style={{ borderColor: 'var(--color-border)' }} className="relative p-3 border-t">
        <div className={`flex items-center ${isCollapsed ? 'flex-col gap-2' : 'gap-2'}`}>
          <div className="flex-1 min-w-0" data-tour="user-profile">
            <UserProfile isCollapsed={isCollapsed} />
          </div>
          {/* Divider between profile and bell. Vertical when the sidebar
              is expanded (horizontal row layout), horizontal when collapsed
              (vertical stack). Subtle separation so the two clickable
              targets don't read as one combined control. */}
          <div
            className={isCollapsed ? 'w-6 h-px' : 'w-px h-6'}
            style={{ backgroundColor: 'var(--color-border)' }}
            aria-hidden="true"
          />
          <div data-tour="notifications">
            <NotificationBell isCollapsed={isCollapsed} />
          </div>
        </div>
      </div>
    </nav>
    </>
  );
};

export default Navbar;
